import careManagementHero from "../../assets/design/care-management-hero.png";
import careManagementHero2x from "../../assets/design/care-management-hero@2x.png";
import "./CareManagerPremiumSection.css";

const SERVICES = [
  {
    id: "staffing",
    icon: "staff",
    title: "Staffing Coordination",
    desc: "Assistance with coordinating care staff according to your agreed requirements.",
  },
  {
    id: "scheduling",
    icon: "chart",
    title: "Scheduling Support",
    desc: "Help with organising care schedules and staffing arrangements.",
  },
  {
    id: "contact",
    icon: "headset",
    title: "Coordination Assistance",
    desc: "A point of contact for coordination-related queries, according to the agreed service scope.",
  },
];

function ServiceIcon({ name }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.75",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    className: "cm-premium__icon-svg",
  };

  if (name === "staff") {
    return (
      <svg {...common}>
        <circle cx="9" cy="8" r="3.25" />
        <circle cx="16.5" cy="9" r="2.5" />
        <path d="M3.5 19c0-3 2.4-5 5.5-5s5.5 2 5.5 5" />
        <path d="M14 19c.2-2.2 1.8-3.8 4-4.2 1.5.2 2.8 1.1 3.3 2.4" />
      </svg>
    );
  }
  if (name === "alert") {
    return (
      <svg {...common}>
        <path d="M12 3.5 21 19H3L12 3.5z" />
        <path d="M12 10v4" />
        <circle cx="12" cy="16.5" r="0.9" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (name === "chart") {
    return (
      <svg {...common}>
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="M8 15l3.5-4.5 3 2.5L18 7" />
      </svg>
    );
  }
  if (name === "shield") {
    return (
      <svg {...common}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }
  if (name === "clipboard") {
    return (
      <svg {...common}>
        <rect x="6" y="5" width="12" height="16" rx="2" />
        <path d="M9 5V4a2 2 0 012-2h2a2 2 0 012 2v1" />
        <path d="M9 11h6M9 15h4" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M4 12v1a3 3 0 003 3h1v-6H7a3 3 0 00-3 2z" />
      <path d="M20 12v1a3 3 0 01-3 3h-1v-6h1a3 3 0 013 2z" />
      <path d="M8 10V8a4 4 0 018 0v2" />
      <path d="M12 16v3" />
      <path d="M9 19h6" />
    </svg>
  );
}

function PeopleBadgeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="9" cy="8" r="3.25" />
      <circle cx="16.5" cy="9" r="2.5" />
      <path d="M3.5 19c0-3 2.4-5 5.5-5s5.5 2 5.5 5" strokeLinecap="round" />
      <path d="M14 19c.2-2.2 1.8-3.8 4-4.2" strokeLinecap="round" />
    </svg>
  );
}

function ArrowChip() {
  return (
    <span className="cm-premium__tile-arrow" aria-hidden>
      <svg viewBox="0 0 20 20" fill="none" aria-hidden>
        <path d="m8 5 5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

export default function CareManagerPremiumSection({ onAddCareManager }) {
  return (
    <section className="cm-premium" aria-labelledby="cm-premium-title">
      <div className="cm-premium__shell">
        <div className="cm-premium__intro">
          <p className="cm-premium__eyebrow">OPTIONAL — PREMIUM SUPPORT</p>
          <h2 id="cm-premium-title" className="cm-premium__title">
            Prefer Help With Care Coordination? Add a <em>Care Manager.</em>
          </h2>
          <p className="cm-premium__lead">
            Direct hiring gives you control over choosing your care professional. If you need
            additional assistance, you can explore our optional Care Manager service for staffing
            and scheduling coordination.
          </p>
          <button type="button" className="cm-premium__cta" onClick={onAddCareManager}>
            <span className="cm-premium__cta-icon" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                <path d="M5 20c0-4 3.1-6 7-6s7 2 7 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            Explore Care Manager Services
            <span className="cm-premium__cta-arrow" aria-hidden>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden>
                <path d="m8 5 5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>

          <div className="cm-premium__photo-wrap">
            <div className="cm-premium__badge">
              <span className="cm-premium__badge-icon" aria-hidden>
                <PeopleBadgeIcon />
              </span>
              <strong>100%</strong>
              <span>Dedicated Care Management</span>
            </div>
            <img
              src={careManagementHero}
              srcSet={`${careManagementHero} 1024w, ${careManagementHero2x} 1024w`}
              sizes="(max-width: 980px) 100vw, 460px"
              alt="NEMA Care Manager supporting families from a care desk"
              className="cm-premium__photo"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>

        <div className="cm-premium__aside">
          <h3 className="cm-premium__aside-title">What your Care Manager handles</h3>
          <div className="cm-premium__grid">
            {SERVICES.map((service) => (
              <article key={service.id} className="cm-premium__tile">
                <span className="cm-premium__tile-icon">
                  <ServiceIcon name={service.icon} />
                </span>
                <h4 className="cm-premium__tile-title">{service.title}</h4>
                <p className="cm-premium__tile-desc">{service.desc}</p>
                <ArrowChip />
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
